// File path: src/hooks/coinglass/useFundingRateData.ts

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { fundingRateHistoryQueryOptions } from "@/services/queries/coinglass/coinglassQueries";
import type { FundingRateDataPoint } from "@/types/coinglass";
import { dateIntToTimestamp } from "@/utils/coinglass";

/**
 * Custom hook for fetching funding rate history data
 *
 * @param ticker - Token ticker symbol (e.g., "BTC")
 * @param exchanges - Array of exchange names (e.g., ["binance", "bybit"])
 * @returns Query result with processed funding rate data
 */
export function useFundingRateData(ticker: string, exchanges: string[]) {
  const query = useQuery(
    fundingRateHistoryQueryOptions({
      ticker,
      exchanges,
    })
  );

  // Process raw API data into chart-ready format
  const processedData = useMemo<FundingRateDataPoint[]>(() => {
    if (!query.data) return [];

    const apiData = query.data;
    const symbolData = apiData.series[ticker];

    if (!symbolData) return [];

    // Get all unique timestamps across all exchanges
    const timestampSet = new Set<number>();
    Object.values(symbolData).forEach((exchangeData) => {
      exchangeData.forEach(([dateInt]) => {
        timestampSet.add(dateIntToTimestamp(dateInt));
      });
    });

    const timestamps = Array.from(timestampSet).sort((a, b) => a - b);

    // Build data points
    const dataPoints: FundingRateDataPoint[] = timestamps.map((timestamp) => {
      const exchanges: { [exchangeName: string]: number } = {};

      // For each exchange, find the funding rate for this timestamp
      Object.entries(symbolData).forEach(([exchangeName, exchangeData]) => {
        const dataPoint = exchangeData.find(
          ([dateInt]) => dateIntToTimestamp(dateInt) === timestamp
        );
        if (dataPoint) {
          exchanges[exchangeName] = dataPoint[1]; // fundingRate
        }
      });

      return {
        timestamp,
        price: null, // Will be populated by price data
        exchanges,
      };
    });

    return dataPoints;
  }, [query.data, ticker]);

  return {
    ...query,
    data: processedData,
  };
}
